import { CustomCellRendererProps, CustomHeaderProps } from 'ag-grid-react';
import { useEffect, useState } from 'react';
import { BNGridCheckbox } from '../../components/GridCheckbox/GridCheckbox';

/**
 * Custom selection checkbox rendered inside the existing drag/drop column.
 * AG Grid v33+ removed per-column `checkboxSelection`, and its replacement
 * (the dedicated selection column) renders an extra always-present column.
 * To keep selection in the column we already control, this drives the grid's
 * native row selection directly via the row node.
 */
export function SelectionCell({ node }: CustomCellRendererProps) {
  const [checked, setChecked] = useState(() => node.isSelected() ?? false);
  useEffect(() => {
    const handler = () => setChecked(node.isSelected() ?? false);
    node.addEventListener('rowSelected', handler);
    return () => node.removeEventListener('rowSelected', handler);
  }, [node]);
  // Keyed by checked state so React mounts a fresh input on each flip. The box is controlled by
  // `checked` but driven via onClick (not onChange), so React's value tracker can skip syncing the
  // DOM .checked of the box just clicked, leaving the row selected but the box visually unchecked.
  // Same fix as Inventory.DragCell.
  return <BNGridCheckbox key={checked ? 'checked' : 'unchecked'} checked={checked} readOnly pt={0} pl={4} size="xs" onClick={() => node.setSelected(!node.isSelected())} aria-label="Select row" />;
}

/** Header select-all checkbox to match the old `headerCheckboxSelection` behavior. */
export function SelectionHeader({ api }: CustomHeaderProps) {
  const [state, setState] = useState({ checked: false, indeterminate: false });
  useEffect(() => {
    const update = () => {
      const selected = api.getSelectedNodes().length;
      const total = api.getDisplayedRowCount();
      setState({ checked: total > 0 && selected >= total, indeterminate: selected > 0 && selected < total });
    };
    api.addEventListener('selectionChanged', update);
    api.addEventListener('modelUpdated', update);
    update();
    return () => {
      api.removeEventListener('selectionChanged', update);
      api.removeEventListener('modelUpdated', update);
    };
  }, [api]);
  return (
    <BNGridCheckbox
      // Keyed by state so React mounts a fresh input on each flip (see SelectionCell above).
      key={`${state.checked}-${state.indeterminate}`}
      checked={state.checked}
      indeterminate={state.indeterminate}
      readOnly
      pt={0}
      pl={4}
      size="xs"
      onClick={() => (state.checked ? api.deselectAll() : api.selectAll())}
      aria-label="Select all rows"
    />
  );
}
